import { getEditRequestReasonTooltipContent } from '@libs/shared/common/helpers/format-edit-request-reason-tooltip.helper';
import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import {
  CampaignRequestEditStatusEnum,
  client,
  HttpException,
  type components,
  type operations,
} from '@/libs/openapi';
import { fromApiCampaignEditRequestFieldAdapter } from '@/resources/campaign-edit-request/campaign-edit-request.field-adapter';
import { Resource } from '@/types/resource.type';
import { toUpperDenomination } from '@/utils/convert.util';
import { formatDate, formatDateInTimezone, formatNumber } from '@/utils/format.util';
import { timezone } from '@/utils/timezone.util';

export const campaignEditRequestQueries = queryNamespace(Resource.campaignEditRequest, {
  detail: (id: number) => queryOptions({
    queryKey: [ id ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/campaign-requests/edits/{id}', {
          signal,
          params: { path: { id }},
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
    select: (data) => ({
      original: data,
      transformed: {
        forLabels: fromApiCampaignEditRequestFieldAdapter.transform(data),
        forForm: fromApiCampaignEditRequestFieldAdapter.transform(data, 'forFormValue'),
      },
      meta: {
        createdByFullName: fromApiCampaignEditRequestFieldAdapter.getCreatedByFullName(data),
      },
    }),
  }),
  list: (campaignRequestId: number, params?: {
    campaignTimezone?: string;
    filter?: Omit<operations['CampaignRequestEditController_getMany']['parameters']['query'], 'limit' | 'page' | 'sortBy'>;
    sorting?: { id: string; desc: boolean };
    paging?: { pageIndex: number; pageSize: number };
  }) => queryOptions({
    queryKey: [ campaignRequestId, params ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/campaign-requests/{id}/edits', {
          signal,
          params: {
            path: { id: campaignRequestId },
            query: {
              limit: params?.paging?.pageSize ?? 10,
              page: (params?.paging?.pageIndex ?? 0) + 1,
              sortBy: params?.sorting
                ? [ `${params.sorting.id}:${params.sorting.desc ? 'DESC' : 'ASC'}` ]
                : undefined,
              ...params?.filter,
            },
          },
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }

          return {
            items: data.items,
            totalCount: data.totalCount,
          };
        }),
    select: (data) => {
      const campaignTimezone = params?.campaignTimezone ?? timezone.get();
      const formatEndDate = formatDateInTimezone('full', campaignTimezone);

      return {
        totalCount: data.totalCount,
        data: data.items.map((row) => {
          const forLabels = fromApiCampaignEditRequestFieldAdapter.transform(row);
          const forForm = fromApiCampaignEditRequestFieldAdapter.transform(row, 'forFormValue');
          const createdByFullName = fromApiCampaignEditRequestFieldAdapter.getCreatedByFullName(row);

          const changeGroups: {
            key: string;
            label: string;
            items: Array<{ key: string; description: string }>
          }[] = [];

          if (row.pauseCampaign || row.campaignStatusPaused) {
            changeGroups.push({
              key: 'status',
              label: 'Pause request',
              items: [{ key: 'status', description: 'Pause request' }],
            });
          }

          const budgetItems: Array<{ key: string; description: string }> = [];

          if (row.newTotalBudget != null && row.newTotalBudget !== row.originalTotalBudget) {
            const newAmount = `${formatNumber(toUpperDenomination(row.newTotalBudget), 'currency')} USD`;
            const originalAmount = row.originalTotalBudget != null
              ? `${formatNumber(toUpperDenomination(row.originalTotalBudget), 'currency')} USD`
              : null;
            const description = originalAmount != null
              ? `Total budget from ${originalAmount} to ${newAmount}`
              : `Total budget to ${newAmount}`;
            budgetItems.push({ key: 'total-budget', description });
          }

          if (row.newMonthlyBudget != null) {
            for (const [ monthKey, amount ] of Object.entries(row.newMonthlyBudget)) {
              if (row.originalMonthlyBudget?.[monthKey] === amount) {
                continue;
              }
              const [ year, month ] = monthKey.split('-');
              const monthLabel = formatDate('short')(new Date(Number(year), Number(month) - 1, 15));
              const newAmount = `${formatNumber(toUpperDenomination(amount), 'currency')} USD`;
              const originalMonthAmount = row.originalMonthlyBudget?.[monthKey] != null
                ? `${formatNumber(toUpperDenomination(row.originalMonthlyBudget[monthKey]), 'currency')} USD`
                : null;
              const description = originalMonthAmount != null
                ? `${monthLabel} from ${originalMonthAmount} to ${newAmount}`
                : `${monthLabel} to ${newAmount}`;
              budgetItems.push({ key: `monthly-${monthKey}`, description });
            }
          }

          if (budgetItems.length > 0) {
            changeGroups.push({
              key: 'budget',
              label: 'Budget changes',
              items: budgetItems,
            });
          }

          if (row.newEndDate != null && row.newEndDate !== row.originalEndDate) {
            const newDate = formatEndDate(row.newEndDate);
            const originalDate = row.originalEndDate != null ? formatEndDate(row.originalEndDate) : null;
            const endDateDescription = originalDate != null
              ? `End date from ${originalDate} to ${newDate}`
              : `End date to ${newDate}`;
            changeGroups.push({
              key: 'end-date',
              label: endDateDescription,
              items: [{ key: 'end-date', description: endDateDescription }],
            });
          }

          if (changeGroups.length === 0) {
            changeGroups.push({ key: 'unknown', label: '', items: [{ key: 'unknown', description: '' }] });
          }

          const totalItems = changeGroups.reduce((sum, g) => sum + g.items.length, 0);
          const isExpandable = totalItems > 1;

          let summaryLabel: string;
          if (!isExpandable) {
            summaryLabel = changeGroups[0].items[0].description;
          } else if (changeGroups.length === 1) {
            summaryLabel = changeGroups[0].label;
          } else {
            summaryLabel = 'Multiple changes';
          }

          const declineReason = row.status === CampaignRequestEditStatusEnum.DECLINED
            ? row.declineReason ?? null
            : null;

          return {
            original: row,
            transformed: {
              forLabels: forLabels,
              forForm: forForm,
            },
            meta: {
              createdByFullName,
              rowId: String(row.id),
              summaryLabel,
              isExpandable,
              changeGroups,
              declineReason,
              reasonTooltip: getEditRequestReasonTooltipContent(row.pauseCampaignReason, declineReason),
            },
          };
        }),
      };
    },
  }),
});

export const campaignEditRequestMutations = mutationNamespace(Resource.campaignEditRequest, {
  create: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['CampaignRequestEditDto'] }) =>
      client
        .POST('/customer/api/tcd/campaign-requests/{id}/edits', { params: { path: { id }}, body })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
  }),
  update: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['CampaignRequestEditDto'] }) =>
      client
        .PATCH('/customer/api/tcd/campaign-requests/edits/{id}', { params: { path: { id }}, body })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
  }),
  cancel: mutationOptions({
    mutationFn: ({ id }: { id: number }) =>
      client
        .POST('/customer/api/tcd/campaign-requests/edits/{id}/cancel', { params: { path: { id }}})
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
  }),
});
